import React, { useState } from "react";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/material.css";
import { useMutation } from "@tanstack/react-query";
import { useDispatch } from "react-redux";
import BaseCard from "../../../components/ui/BaseCard";
import BaseTextField from "../../../components/ui/BaseTextField";
import BaseButton from "../../../components/ui/BaseButton";
import AddEditPersonalModal from "./components/AddEditPersonalModal";
import ProfilePictureAvatar from "../../../components/ui/ProfilePictureAvatar";
import { updateUserDetails } from "../../../services/api/user";
import { showToast } from "../../../store/toast/toastSlice";
import { formatPhoneNumber, normalizePhoneNumber } from "../../../utils/helpers";
import { UserType } from "../../../utils/enum";

dayjs.extend(relativeTime);

const SELF_ROLES = [UserType.FREELANCER, UserType.CLIENT, UserType.CUSTOMER];

const AddEditPersonal = ({
  firstName,
  lastName,
  phoneNumber,
  email,
  authRole,
  userId,
  profilePicture,
  refetchMe,
  createdAt,
  lastActive,
}) => {
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  // "name" or "phone" while that row is being edited.
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState({ firstName: "", lastName: "", phoneNumber: "" });

  // Names take letters and spaces only, matching the signup forms.
  const setNamePart = (field) => (e) => {
    if (/^[A-Za-z\s]*$/.test(e.target.value)) {
      setDraft({ ...draft, [field]: e.target.value });
    }
  };

  const isSelf = SELF_ROLES.includes(authRole);

  const { mutateAsync: updateUser, isPending: isUpdating } = useMutation({
    mutationFn: updateUserDetails,
  });

  const handleOpen = () => setOpen(true);
  const handleClose = () => !saving && setOpen(false);

  const startEditing = (field) => {
    setDraft({
      firstName: firstName || "",
      lastName: lastName || "",
      phoneNumber: phoneNumber || "",
    });
    setEditing(field);
  };

  const save = async (payload) => {
    try {
      await updateUser(payload);
      await refetchMe?.();
      setEditing(null);
      dispatch(showToast({ type: "success", message: "Profile updated successfully" }));
    } catch (err) {
      dispatch(
        showToast({
          type: "error",
          message: err?.response?.data?.message || "Failed to update profile.",
        }),
      );
    }
  };

  const row = (label, value, action, isLast = false) => (
    <div
      className={`grid grid-cols-1 md:grid-cols-12 gap-1 items-center pb-2 pt-4 ${
        isLast ? "" : "border-b"
      }`}
    >
      <p className="font-normal md:col-span-2 text-sm">{label}</p>
      <div className="md:col-span-7">{value}</div>
      <div className="md:col-span-3 md:text-right">{action}</div>
    </div>
  );

  const editIcon = (field) =>
    isSelf && (
      <i
        className="fa-light fa-pencil text-gray-900 cursor-pointer"
        onClick={() => startEditing(field)}
      ></i>
    );

  const saveActions = (payload, isValid) => (
    <div className="flex md:justify-end gap-2">
      <BaseButton
        variant="outlined"
        color="primary"
        className="py-[6px] px-[8px]"
        disabled={isUpdating}
        onClick={() => setEditing(null)}
      >
        Cancel
      </BaseButton>
      <BaseButton
        color="primary"
        className="py-[6px] px-[8px]"
        isLoading={isUpdating}
        loaderSize="18px"
        disabled={!isValid || isUpdating}
        onClick={() => save(payload)}
      >
        Save
      </BaseButton>
    </div>
  );

  return (
    <>
      <BaseCard className="py-2 px-4 sm:px-6 rounded-lg" elevation={1}>
        <div className="bg-light px-4 py-2 mt-2 mb-2 rounded-md flex flex-wrap justify-between items-center gap-2">
          <h6 className="text-dark font-normal">Personal Information</h6>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-sm">
            <p>
              <span className="text-gray-500">Created On:</span>{" "}
              {createdAt ? dayjs(createdAt).format("YYYY/MM/DD hh:mm A") : "—"}
            </p>
            {/* Null for someone who has never had a session. */}
            <p>
              <span className="text-gray-500">Last Active:</span>{" "}
              {lastActive ? dayjs(lastActive).fromNow() : "—"}
            </p>
            {authRole === UserType.ADMIN && (
              <i
                className="fa-light fa-pencil text-gray-900 cursor-pointer"
                onClick={() => handleOpen()}
              ></i>
            )}
          </div>
        </div>

        {/* Inset to the header's padding, so the row pencils line up with its icons. */}
        <div className="flex flex-col sm:flex-row gap-6 sm:gap-8 px-4">
          <div className="w-full sm:w-[170px] shrink-0 flex flex-col items-center justify-center border-b sm:border-b-0 sm:border-r pb-4 sm:pb-0 sm:pr-8">
            <ProfilePictureAvatar
              size={84}
              profilePicture={profilePicture}
              firstName={firstName}
              lastName={lastName}
              refetchMe={refetchMe}
            />
            <p className="text-xs text-gray-500 mt-2">Profile Photo</p>
          </div>

          <div className="flex-1 min-w-0">
            {row(
              "Email Address",
              <p className="text-sm">{email}</p>,
              isSelf && (
                <p className="text-sm">
                  <span
                    className="text-primary cursor-pointer hover:underline"
                    onClick={() =>
                      window.open(
                        `mailto:helpdesk@techdomain.com.au?subject=Email Address Change Request&body=Hi, I would like to change the email address on my account. Current email address: ${email}.`,
                      )
                    }
                  >
                    Contact Support
                  </span>{" "}
                  to change the email address
                </p>
              ),
            )}

            {editing === "name"
              ? row(
                  "Legal Name",
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <BaseTextField
                      id="firstName"
                      name="firstName"
                      placeholder="First Name"
                      optional={false}
                      value={draft.firstName}
                      onChange={setNamePart("firstName")}
                      fullWidth
                    />
                    <BaseTextField
                      id="lastName"
                      name="lastName"
                      placeholder="Last Name"
                      optional={false}
                      value={draft.lastName}
                      onChange={setNamePart("lastName")}
                      fullWidth
                    />
                  </div>,
                  saveActions(
                    { firstName: draft.firstName.trim(), lastName: draft.lastName.trim() },
                    draft.firstName.trim() !== "",
                  ),
                )
              : row(
                  "Legal Name",
                  <p className="text-sm">{`${firstName} ${lastName}`}</p>,
                  editIcon("name"),
                )}

            {editing === "phone"
              ? row(
                  "Phone",
                  <PhoneInput
                    specialLabel=""
                    country={"au"}
                    value={draft.phoneNumber}
                    // PhoneInput reports bare digits; the country restores the dialling
                    // code, so the number is stored the same way an admin edit stores it.
                    onChange={(val, country) =>
                      setDraft({ ...draft, phoneNumber: normalizePhoneNumber(val, country) })
                    }
                    dropdownStyle={{
                      maxHeight: "150px",
                      position: "absolute",
                      top: "auto",
                      bottom: "100%",
                      margin: 0,
                      zIndex: 30,
                    }}
                    inputStyle={{ height: "40px", width: "100%", borderRadius: "8px" }}
                    containerClass="w-full max-w-[320px]"
                  />,
                  saveActions({ phoneNumber: draft.phoneNumber }, draft.phoneNumber !== ""),
                  true,
                )
              : row(
                  "Phone",
                  <p className="text-sm">{formatPhoneNumber(phoneNumber) || "—"}</p>,
                  editIcon("phone"),
                  true,
                )}
          </div>
        </div>
      </BaseCard>

      <AddEditPersonalModal
        open={open}
        close={handleClose}
        isSaving={saving}
        userId={userId}
        initialValues={{ firstName, lastName, email, phoneNumber, role: authRole }}
        refetchMe={refetchMe}
      />
    </>
  );
};

export default AddEditPersonal;
